import './style.css';
import { loadRegistry } from './registry.js';
import { Store } from './store.js';
import { createGraph } from './graph.js';
import { createTable } from './table.js';
import { createFeed } from './feed.js';
import { createStats } from './stats.js';
import { createLegend } from './legend.js';
import { backfill, subscribe } from './ledger.js';
import { createReceipts, backfillReceipts, subscribeReceipts } from './receipts.js';
import { initTxModal, openTxModal } from './tx-modal.js';
import { saveSnapshot } from './cache.js';

const app = document.getElementById('app');

app.innerHTML = `
  <header class="header">
    <h1 class="header__title">Simples Coin · Observer</h1>
    <span class="badge">⚠ Simples Ledger v0 · rede interna (sem valor real)</span>
    <div class="header__spacer"></div>
    <a class="header__link" href="https://coin.simples.media" target="_blank" rel="noopener">Paper →</a>
    <button class="theme-toggle" type="button" id="theme-toggle">Modo claro</button>
  </header>
  <main>
    <p class="ecosystem-strip"><strong>A economia dos agentes está começando — e cada pagamento aqui é real.</strong>
      Os 40+ agentes da Simples Media já têm carteira SIMC e pagam quando
      consomem um serviço de verdade. Sem tráfego simulado: no começo o grafo
      fica quieto, e é exatamente por isso que dá pra confiar nele.</p>
    <section class="stats" id="stats"></section>
    <section class="content">
      <div class="panel">
        <div class="panel__header">
          <p class="panel__title">Economia agêntica ao vivo</p>
          <div class="panel__spacer"></div>
          <div class="view-toggle" id="view-toggle">
            <button type="button" data-view="graph" class="active">Grafo</button>
            <button type="button" data-view="table">Tabela</button>
          </div>
        </div>
        <div class="graph-wrap" id="graph-wrap"></div>
        <div class="table-wrap" id="table-wrap" style="display:none"></div>
        <div class="legend" id="legend"></div>
      </div>
      <div class="panel">
        <div class="panel__header">
          <p class="panel__title">Últimos pagamentos</p>
        </div>
        <div class="feed" id="feed"></div>
      </div>
    </section>
    <section class="panel receipts-panel">
      <div class="panel__header">
        <p class="panel__title">Recibos de interação ao vivo</p>
        <span class="panel__hint">STT · encaminhamento · processamento agêntico — cache costuma dominar</span>
      </div>
      <div class="receipts" id="receipts"></div>
    </section>
  </main>
  <footer class="footer">
    <span>Rede de testes — sem valor real. Um projeto Simples Media.</span>
    <a href="https://github.com/simples-media/simplescoin" target="_blank" rel="noopener">GitHub →</a>
  </footer>
`;

// ---------- Tema ----------

const themeToggle = document.getElementById('theme-toggle');
function applyTheme(theme) {
  if (theme) {
    document.documentElement.setAttribute('data-theme', theme);
  } else {
    document.documentElement.removeAttribute('data-theme');
  }
  themeToggle.textContent = currentIsLight() ? 'Modo escuro' : 'Modo claro';
}
function currentIsLight() {
  const explicit = document.documentElement.getAttribute('data-theme');
  if (explicit) return explicit === 'light';
  return window.matchMedia('(prefers-color-scheme: light)').matches;
}
themeToggle.addEventListener('click', () => {
  const next = currentIsLight() ? 'dark' : 'light';
  applyTheme(next);
  localStorage.setItem('observer-theme', next);
});
applyTheme(localStorage.getItem('observer-theme') ?? null);

// ---------- View toggle (Grafo | Tabela) ----------

const graphWrap = document.getElementById('graph-wrap');
const tableWrap = document.getElementById('table-wrap');
document.getElementById('view-toggle').addEventListener('click', (event) => {
  const btn = event.target.closest('button[data-view]');
  if (!btn) return;
  const view = btn.dataset.view;
  document.querySelectorAll('#view-toggle button').forEach((b) => b.classList.toggle('active', b === btn));
  graphWrap.style.display = view === 'graph' ? '' : 'none';
  tableWrap.style.display = view === 'table' ? '' : 'none';
});

// ---------- Boot ----------

function renderOffline(stats, registry) {
  graphWrap.innerHTML = `
    <div class="empty-state">
      <strong>Ledger offline</strong>
      Não foi possível conectar ao nó do Simples Ledger. Assim que ele
      responder, o Observer volta a plotar a economia agêntica ao vivo.
    </div>
  `;
  tableWrap.innerHTML = `<div class="empty-state">Ledger offline.</div>`;
  document.getElementById('feed').innerHTML = `<div class="feed-empty">Ledger offline — sem dados no momento.</div>`;
  stats.render(new Store(registry));
}

async function main() {
  const [config, registry] = await Promise.all([
    fetch('/config.json').then((r) => r.json()),
    loadRegistry(),
  ]);

  const apiUrl = config.apiPublic || config.api;

  const statsEl = document.getElementById('stats');
  const legendEl = document.getElementById('legend');
  const feedEl = document.getElementById('feed');

  const stats = createStats(statsEl);
  const legend = createLegend(legendEl);
  legend.render(registry);

  let head;
  try {
    const res = await fetch(`${apiUrl}/head`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    head = await res.json();
  } catch (err) {
    console.error('[observer] ledger offline', err);
    renderOffline(stats, registry);
    return;
  }

  initTxModal(apiUrl);

  const store = new Store(registry);
  store.supply = head.supply != null ? BigInt(head.supply) : null;

  const openTx = (hash) => {
    const payment = store.payments.find((p) => p.hash === hash);
    if (payment) openTxModal(payment, store);
  };

  const table = createTable(tableWrap, { onOpenTx: openTx });
  const feed = createFeed(feedEl, { onOpenTx: openTx });
  const graph = createGraph(graphWrap, store);
  const receipts = createReceipts(document.getElementById('receipts'));
  receipts.render();

  // Zero-state honesto: nó no ar mas sem pagamentos reais ainda — o grafo
  // quieto é proposital (só transações genuínas entram; decisão do Gule).
  graphWrap.style.position = graphWrap.style.position || 'relative';
  const zeroState = document.createElement('div');
  zeroState.className = 'empty-state';
  zeroState.style.cssText = 'position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;pointer-events:none;';
  zeroState.innerHTML = `<strong>A economia começou — e está quieta de propósito.</strong>
    <span>Cada pagamento que aparecer aqui será real: um agente consumindo um serviço de verdade. Sem simulação.</span>`;
  graphWrap.appendChild(zeroState);

  function renderAll() {
    stats.render(store);
    legend.render(registry);
    table.render(store);
    feed.render(store);
    graph.syncData();
    zeroState.style.display = store.payments.length === 0 ? 'flex' : 'none';
  }

  // Partículas/pulso do grafo só no fluxo ao vivo — nunca no backfill inicial.
  let live = false;

  store.addEventListener('payments', (event) => {
    renderAll();
    saveSnapshot(store.payments);
    if (!live) return;
    for (const p of event.detail.added) {
      graph.onNewPayment(p);
    }
  });

  renderAll();
  setInterval(() => { feed.tick(); receipts.tick(); }, 1000);

  // Recibos agênticos: backfill + SSE independentes do fluxo do ledger.
  backfillReceipts(apiUrl)
    .then((items) => receipts.addMany(items))
    .catch((err) => console.error('[observer] backfill de recibos falhou', err));
  subscribeReceipts(apiUrl, {
    onReceipt: (r) => receipts.addOne(r),
    onStatus: (status) => console.log(`[observer] receipts stream: ${status}`),
  });

  // Backfill histórico, paginado, do log inteiro do nó.
  try {
    await backfill(apiUrl, (page) => store.addPayments(page));
  } catch (err) {
    console.error('[observer] falha no backfill histórico', err);
  }

  live = true;
  setTimeout(() => graph.fitView(600), 800); // enquadra depois da física assentar

  // Tempo real: SSE com reconexão, fallback pra polling se o stream cair.
  subscribe(apiUrl, {
    lastIdx: store.lastIdx,
    onTx: (tx) => store.addPayments([tx]),
    onStatus: (status) => console.log(`[observer] ledger stream: ${status}`),
  });
}

main().catch((err) => {
  console.error('[observer] falha ao iniciar', err);
  graphWrap.innerHTML = `<div class="empty-state"><strong>Erro ao carregar o Observer</strong>${err.message}</div>`;
});
